import { List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { CreateExplorerEvent } from '~commonInfra/telemetryEvents/TelemetryEventCreator';
import { ExplorerDisplayMode, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { TallListItemCheckmarkCentered } from '~src/commonInfra/styles/CommonStyles';

import { Messages } from '../../../codegen/Localize';
import { DisplayMode } from '../../../sessionManagement/serverApi/types/ISessionInfo';
import { ExplorerStyles } from './ExplorerStyles';
import { ExplorerToolbarTestIds } from './ExplorerToolbar';
import { ExplorerDropdownStyles } from './widgets/ExplorerDropdownStyles';

export namespace ExplorerViewModeSwitcherTestIds {
  export const CardButton = 'view-mode-toggle-thumbnail';
  export const ListButton = 'view-mode-toggle-list';
  export const MainButton = 'view-mode-toggle';
}

interface ViewModeInfo {
  mode: DisplayMode;
  icon: (fill: string) => string;
  testId: string;
  displayValue: () => string;
}

const encodeSvg = (svg: string): string => `data:image/svg+xml,${encodeURIComponent(svg)}`;
const ThumbnailModeIconSvg = (fill: string = '#666') => `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path fill="${fill}" d="M3 3h3v3H3zm4 0h3v3H7zm4 0h3v3h-3zM3 7h3v3H3zm4 0h3v3H7zm4 0h3v3h-3zm-8 4h3v3H3zm4 0h3v3H7zm4 0h3v3h-3z"/></svg>`;
const ListModeIconSvg = (fill: string = '#666') => `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"><path fill="${fill}" d="M3 3h3v3H3zM7 3h8.1v3H7zM3 7h3v3H3zM7 7h8.1v3H7zM3 11h3v3H3zM7 11h8.1v3H7z"/></svg>`;
const ViewModeInfos: ViewModeInfo[] = [
  {
    mode: 'thumbnail',
    icon: ThumbnailModeIconSvg,
    testId: ExplorerViewModeSwitcherTestIds.CardButton,
    displayValue: Messages.explorerViewMode_thumbnail
  },
  {
    mode: 'list',
    icon: ListModeIconSvg,
    testId: ExplorerViewModeSwitcherTestIds.ListButton,
    displayValue: Messages.explorerViewMode_list
  }
];

export interface ExplorerViewModeSwitcherProps {
  readonly currentViewMode: DisplayMode;
  readonly changeViewMode: (viewMode: DisplayMode) => void;
  readonly disabled: boolean;
}

export class ExplorerViewModeSwitcher extends React.PureComponent<ExplorerViewModeSwitcherProps> {

  private handleChangeViewMode = (mode: DisplayMode): void => {
    this.props.changeViewMode(mode);

    Telemetry.sendEvent<ExplorerDisplayMode>(CreateExplorerEvent<ExplorerDisplayMode>(
      {
        type: TelemetryEvents.ExplorerDisplayMode,
        previousMode: this.props.currentViewMode,
        newMode: mode
      }
    ));
  };

  private encodeIconForMode = (modeInfo: ViewModeInfo) => encodeSvg(modeInfo.icon(this.props.disabled ? '#bfbfbf' : '#666'));
  private renderModeIcon = (mode: ViewModeInfo, alt?: string) => <img
    alt={alt}
    aria-hidden={alt ? undefined : true}
    className={ExplorerStyles.ExplorerViewModeMainButtonImg}
    src={this.encodeIconForMode(mode)}
  />;

  public render(): JSX.Element {
    const currentMode: ViewModeInfo | undefined = _.find(ViewModeInfos, (info) => info.mode === this.props.currentViewMode); // can be undefined in tests
    return (
      <SingleSelectDropdown
        aria-label={Messages.explorerViewMode()}
        aria-labelledby={null}
        children={currentMode && this.renderModeIcon(currentMode, currentMode?.displayValue())}
        disabled={this.props.disabled}
        label=''
        list={<List
          aria-label={Messages.explorerViewMode()}
          aria-labelledby={null}
          className={TallListItemCheckmarkCentered}
          items={ViewModeInfos.map<ListItem>((info: ViewModeInfo) => ({
            value: info.mode,
            content: <div className={ExplorerDropdownStyles.ExplorerDropdownListItemContainer}>
              {info.displayValue()}
              {this.renderModeIcon(info)}
            </div>,
            testId: info.testId + '-MenuItem', // not actually a menuitem, but this keeps backwards-compatibility with tests
          }))}
          style={{ minWidth: 150 }}
        />}
        onChange={this.handleChangeViewMode}
        testId={ExplorerToolbarTestIds.ViewModeSwitcher}
        title={Messages.explorerViewMode()}
        value={this.props.currentViewMode}
      />
    );
  }
}
